import { reactive, computed } from 'vue';

const state = reactive({
    catalog: { plans: [], addons: [], associations: [] },
    catalogLoaded: false,
    catalogError: null,

    currentStep: 1,
    totalSteps: 5,
    // Drives the slide-direction CSS for the step transition. goNext = 'forward', goBack = 'backward'.
    stepDirection: 'forward',

    submitting: false,
    submitted: false,
    submitError: null,
    serverErrors: {},

    form: {
        name: '',
        email: '',
        phone: '',
        brokerage: '',
        principalBrokerName: '',
        principalBrokerEmail: '',
        website: '',
        websiteBuilder: '',
        websiteBuilderOther: '',
        signupNotes: '',
        planCode: 'idx_base',
        addonCodes: [],
        associations: [],
        otherSelected: false,
        otherAssociationName: '',
        companyWebsite: '',
    },
});

////////////////////////////////////////////////////////////////////
/// Live quote — recomputes whenever plan/addons/associations change.
const quote = computed(() => {
    const plan = state.catalog.plans.find((p) => p.code === state.form.planCode);
    if (!plan) {
        return { setup: 0, monthly: 0, additionalCount: 0, additionalFee: 0 };
    }

    const addons = state.catalog.addons.filter((a) => state.form.addonCodes.includes(a.code));
    const assocs = state.catalog.associations.filter((a) =>
        state.form.associations.some((s) => s.id === a.id),
    );

    // Mirrors SignupQuoteService::calculate — first MLS bundled in basePrice;
    // each additional (catalog OR Other) adds the per-additional flat fee.
    // Use the checkbox state so the quote updates the moment the user clicks
    // Other, even before they've typed the freeform name.
    const totalAssocCount = assocs.length + (state.form.otherSelected ? 1 : 0);
    const additionalCount = Math.max(0, totalAssocCount - 1);
    const additionalFee = parseFloat(plan.additionalAssociationMonthly || 0) * additionalCount;

    const setup =
        parseFloat(plan.setupFee || 0) +
        addons.reduce((s, a) => s + parseFloat(a.setupFee || 0), 0) +
        assocs.reduce((s, a) => s + parseFloat(a.setupPassthroughFee || 0), 0);

    const monthly =
        parseFloat(plan.basePrice || 0) +
        addons.reduce((s, a) => s + parseFloat(a.basePrice || 0), 0) +
        assocs.reduce(
            (s, a) =>
                s + parseFloat(a.passthroughFeeMonthly || 0) + parseFloat(a.dataAccessSurchargeMonthly || 0),
            0,
        ) +
        additionalFee;

    return { setup, monthly, additionalCount, additionalFee };
});

////////////////////////////////////////////////////////////////////
/// Per-step "is this step complete enough to advance" check.
/// Returns true when the Next button should be enabled.
const stepIsValid = (step) => {
    const f = state.form;
    if (step === 1) return !!f.planCode;
    if (step === 2) return f.name.trim() && /\S+@\S+\.\S+/.test(f.email) && f.phone.trim();
    if (step === 3) return f.brokerage.trim() && f.principalBrokerName.trim() && /\S+@\S+\.\S+/.test(f.principalBrokerEmail);
    if (step === 4) {
        // Three independent constraints:
        //   1. At least one selection (supported MLS OR Other-with-name)
        //   2. Every selected supported MLS has an agent ID — checking Other
        //      doesn't excuse missing agent IDs on the catalog rows you picked
        //   3. If Other is checked, its name field is filled — so the server-side
        //      quote can't drift from what the live quote panel showed
        const supportedCount = f.associations.length;
        const allSupportedHaveAgentId = f.associations.every((a) => a.id && (a.agentId || '').trim());
        const otherComplete = f.otherSelected && f.otherAssociationName.trim() !== '';
        const otherInvalid = f.otherSelected && f.otherAssociationName.trim() === '';

        const hasAnySelection = supportedCount > 0 || otherComplete;
        return hasAnySelection && allSupportedHaveAgentId && !otherInvalid;
    }
    if (step === 5) return true;  // review step has nothing to validate; submit is always allowed
    return false;
};

////////////////////////////////////////////////////////////////////
/// Navigation helpers.
const goNext = () => {
    if (state.currentStep < state.totalSteps && stepIsValid(state.currentStep)) {
        state.stepDirection = 'forward';
        state.currentStep += 1;
    }
};

const goBack = () => {
    if (state.currentStep > 1) {
        state.stepDirection = 'backward';
        state.currentStep -= 1;
        state.serverErrors = {};
    }
};

////////////////////////////////////////////////////////////////////
/// Toggle an addon code in/out of the form selection.
const toggleAddon = (code) => {
    const idx = state.form.addonCodes.indexOf(code);
    if (idx === -1) {
        state.form.addonCodes.push(code);
    } else {
        state.form.addonCodes.splice(idx, 1);
    }
};

////////////////////////////////////////////////////////////////////
/// Toggle association in/out of selection. Stores { id, agentId } objects.
const toggleAssociation = (id) => {
    const idx = state.form.associations.findIndex((a) => a.id === id);
    if (idx === -1) {
        state.form.associations.push({ id, agentId: '' });
    } else {
        state.form.associations.splice(idx, 1);
    }
};

const setAgentId = (id, agentId) => {
    const row = state.form.associations.find((a) => a.id === id);
    if (row) row.agentId = agentId;
};

export default function useState() {
    return { state, quote, stepIsValid, goNext, goBack, toggleAddon, toggleAssociation, setAgentId };
}
